import { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine, ReferenceDot, Legend,
} from 'recharts';

export interface ChartPoint {
  ts: number;
  price: number;
  ma50: number | null;
  ma200: number | null;
}

interface Props {
  data: ChartPoint[];
  support?: number | null;
  resistance?: number | null;
  phaseLabel: string;
  phaseHsl: string; // e.g. 'var(--primary)' or hsl string
}

const fmtAud = (v: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(v);

const fmtDate = (ts: number) =>
  new Date(ts).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });

const BitcoinPriceMaChart = ({ data, support, resistance, phaseLabel, phaseHsl }: Props) => {
  const { series, crossovers, latest } = useMemo(() => {
    const series = data.filter((d) => Number.isFinite(d.price) && d.price > 0);
    const crossovers: { ts: number; price: number; type: 'golden' | 'death' }[] = [];
    for (let i = 1; i < series.length; i++) {
      const prev = series[i - 1];
      const cur = series[i];
      if (prev.ma50 == null || prev.ma200 == null || cur.ma50 == null || cur.ma200 == null) continue;
      if (prev.ma50 <= prev.ma200 && cur.ma50 > cur.ma200) crossovers.push({ ts: cur.ts, price: cur.price, type: 'golden' });
      else if (prev.ma50 >= prev.ma200 && cur.ma50 < cur.ma200) crossovers.push({ ts: cur.ts, price: cur.price, type: 'death' });
    }
    return { series, crossovers, latest: series[series.length - 1] };
  }, [data]);

  if (series.length === 0) {
    return (
      <div className="bg-muted/20 rounded-lg p-4 text-sm text-muted-foreground">
        Chart data unavailable.
      </div>
    );
  }

  const prices = series.map((d) => d.price);
  const mins = [Math.min(...prices), support ?? Infinity].filter((x) => Number.isFinite(x)) as number[];
  const maxes = [Math.max(...prices), resistance ?? -Infinity].filter((x) => Number.isFinite(x)) as number[];
  const yMin = Math.min(...mins) * 0.97;
  const yMax = Math.max(...maxes) * 1.03;

  return (
    <div className="bg-muted/20 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-sm font-semibold">BTC/AUD — 220 days with MA50 / MA200</div>
        <div className="text-xs text-muted-foreground">Phase: <span className="font-medium">{phaseLabel}</span></div>
      </div>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="btcPriceFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
            <XAxis
              dataKey="ts"
              tickFormatter={fmtDate}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              minTickGap={40}
              stroke="hsl(var(--border))"
            />
            <YAxis
              domain={[yMin, yMax]}
              tickFormatter={(v) => `A$${(v / 1000).toFixed(0)}k`}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              width={56}
              stroke="hsl(var(--border))"
            />
            <Tooltip
              contentStyle={{
                background: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 8,
                fontSize: 12,
              }}
              labelFormatter={(v) => new Date(Number(v)).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
              formatter={(value: number, name: string) => [
                Number.isFinite(value) ? fmtAud(value) : '—',
                name,
              ]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {support != null && Number.isFinite(support) && (
              <ReferenceLine y={support} stroke="hsl(142 70% 45%)" strokeDasharray="4 4" label={{ value: `Support ${fmtAud(support)}`, fontSize: 10, fill: 'hsl(142 70% 45%)', position: 'insideBottomLeft' }} />
            )}
            {resistance != null && Number.isFinite(resistance) && (
              <ReferenceLine y={resistance} stroke="hsl(0 75% 60%)" strokeDasharray="4 4" label={{ value: `Resistance ${fmtAud(resistance)}`, fontSize: 10, fill: 'hsl(0 75% 60%)', position: 'insideTopLeft' }} />
            )}
            <Line type="monotone" dataKey="price" name="BTC/AUD" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} fill="url(#btcPriceFill)" />
            <Line type="monotone" dataKey="ma50" name="MA50" stroke="hsl(45 95% 55%)" strokeWidth={1.5} dot={false} connectNulls />
            <Line type="monotone" dataKey="ma200" name="MA200" stroke="hsl(280 70% 65%)" strokeWidth={1.5} dot={false} connectNulls />
            {crossovers.map((c, i) => (
              <ReferenceDot
                key={i}
                x={c.ts}
                y={c.price}
                r={5}
                fill={c.type === 'golden' ? 'hsl(142 70% 45%)' : 'hsl(0 75% 60%)'}
                stroke="hsl(var(--background))"
                strokeWidth={2}
                ifOverflow="extendDomain"
              />
            ))}
            {latest && (
              <ReferenceDot
                x={latest.ts}
                y={latest.price}
                r={6}
                fill={phaseHsl}
                stroke="hsl(var(--background))"
                strokeWidth={2}
                label={{ value: phaseLabel, position: 'top', fontSize: 10, fill: phaseHsl }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-3 mt-2 px-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: 'hsl(142 70% 45%)' }} /> Golden cross</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: 'hsl(0 75% 60%)' }} /> Death cross</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: phaseHsl }} /> Current phase</span>
        <span>Display in AUD; signal math (Mayer, MA cross) computed in USD to filter FX noise.</span>
      </div>
    </div>
  );
};

export default BitcoinPriceMaChart;
